import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { useLocalStorage } from '@/lib/use-local-storage';

export interface CookiePrefs {
  analytics: boolean;
  marketing: boolean;
}

type ConsentStatus = 'accepted' | 'rejected' | 'customized' | null;

interface StoredConsent {
  status: ConsentStatus;
  prefs: CookiePrefs;
  /** ISO timestamp of the last accept/reject/customize action — an audit
   *  trail proving when consent was given, not just what was chosen. */
  consentedAt: string | null;
  /** Bumped whenever the Cookie Policy's categories materially change, so an
   *  old decision made under a previous policy is treated as stale. */
  version: number;
}

/** Bump this if the Cookie Policy's categories ever materially change. */
const CONSENT_VERSION = 1;

/** Re-ask after ~12 months even if nothing changed — standard practice
 *  (matches the default most consent-management platforms ship with), since
 *  a years-old "accept" shouldn't silently authorize whatever the site does
 *  today. */
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

const DEFAULT_PREFS: CookiePrefs = { analytics: false, marketing: false };
const INITIAL: StoredConsent = {
  status: null,
  prefs: DEFAULT_PREFS,
  consentedAt: null,
  version: CONSENT_VERSION,
};

interface CookieConsentContextValue {
  status: ConsentStatus;
  prefs: CookiePrefs;
  /** True while the banner/preferences modal should be shown — either no
   *  decision has been made yet, or the user reopened it via `openPreferences`. */
  bannerOpen: boolean;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  savePrefs: (prefs: CookiePrefs) => void;
  /** Reopen the banner to revisit an existing choice (e.g. from the Cookie
   *  Policy page's "Manage preferences" button). */
  openPreferences: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

/**
 * Cookie-consent state, persisted client-side (this app has no third-party
 * tracking cookies today — only localStorage-backed features like Wishlist
 * and Compare — so the decision itself is stored the same way, under
 * `rps-cookie-consent`, ahead of any future analytics integration).
 */
export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useLocalStorage<StoredConsent>('rps-cookie-consent', INITIAL);
  const [reopened, setReopened] = useState(false);

  const acceptAll = useCallback(() => {
    setStored({
      status: 'accepted',
      prefs: { analytics: true, marketing: true },
      consentedAt: new Date().toISOString(),
      version: CONSENT_VERSION,
    });
    setReopened(false);
  }, [setStored]);

  const rejectNonEssential = useCallback(() => {
    setStored({
      status: 'rejected',
      prefs: DEFAULT_PREFS,
      consentedAt: new Date().toISOString(),
      version: CONSENT_VERSION,
    });
    setReopened(false);
  }, [setStored]);

  const savePrefs = useCallback(
    (prefs: CookiePrefs) => {
      setStored({ status: 'customized', prefs, consentedAt: new Date().toISOString(), version: CONSENT_VERSION });
      setReopened(false);
    },
    [setStored],
  );

  const openPreferences = useCallback(() => setReopened(true), []);

  // A decision made under an older policy version, or old enough that it
  // shouldn't keep silently authorizing today's behavior, is treated exactly
  // like no decision at all — re-prompted, with non-essential prefs back to
  // their off-by-default state until re-confirmed.
  const isStale =
    stored.status !== null &&
    (stored.version !== CONSENT_VERSION ||
      stored.consentedAt === null ||
      Date.now() - new Date(stored.consentedAt).getTime() > CONSENT_MAX_AGE_MS);

  const effectiveStatus = isStale ? null : stored.status;
  const effectivePrefs = isStale ? DEFAULT_PREFS : stored.prefs;

  const value = useMemo<CookieConsentContextValue>(
    () => ({
      status: effectiveStatus,
      prefs: effectivePrefs,
      bannerOpen: effectiveStatus === null || reopened,
      acceptAll,
      rejectNonEssential,
      savePrefs,
      openPreferences,
    }),
    [effectiveStatus, effectivePrefs, reopened, acceptAll, rejectNonEssential, savePrefs, openPreferences],
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent(): CookieConsentContextValue {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  return ctx;
}
